import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { 
  FaArrowLeft, 
  FaCheckCircle, 
  FaArrowRight, 
  FaDownload, 
  FaPlay,
  FaShare,
  FaStar,
  FaUsers,
  FaCloud,
  FaBuilding,
  FaMobileAlt,
  FaPuzzlePiece,
  FaLink,
  FaUserTie,
  FaHospital,
  FaGraduationCap,
  FaUserFriends,
  FaSchool,
  FaExchangeAlt,
  FaNetworkWired,
  FaShieldAlt,
  FaRocket,
  FaHeadset,
  FaChevronDown,
  FaChevronUp,
  FaUniversity,
  FaMoneyBillWave,
  FaChartLine,
  FaSync,
  FaLock,
  FaCheck,
  FaSpinner,
  FaTimes,
  FaHandshake,
  FaReceipt,
  FaFileInvoice,
  FaBuilding as FaBuildingIcon // Renamed to avoid conflict
} from 'react-icons/fa';
import { productsData } from '@data/productsData';

// ─── Import Local Category Images ──────────────────────────────────
import coreBankingImg from '@assets/corebanking.png';
import erpSystemsImg from '@assets/erpystems.png';
import paymentsImg from '@assets/bankintegration.png';
import healthcareImg from '@assets/hospitalmanagement.png';
import educationImg from '@assets/healthcaresystems.png';
import hrImg from '@assets/hrpayroll.png';
import mobileImg from '@assets/mobilebanking.png';
import aiImg from '@assets/aitraining.png';
import integrationImg from '@assets/bankintegration.png';

// ─── Category Images Mapping ──────────────────────────────────
const categoryImages = {
  'Core Banking': coreBankingImg,
  'ERP': erpSystemsImg,
  'Payments': paymentsImg,
  'Healthcare': healthcareImg,
  'Education': educationImg,
  'HR': hrImg,
  'Mobile': mobileImg,
  'AI': aiImg,
  'Integration': integrationImg,
};

// ─── Product Images Mapping ──────────────────────────────────
const productImages = {
  'core-banking': coreBankingImg,
  'power-financials': coreBankingImg,
  'sacco-management': coreBankingImg,
  'erp-solutions': erpSystemsImg,
  'dynamics-365': erpSystemsImg,
  'dynamics-nav': erpSystemsImg,
  'hospital-management': healthcareImg,
  'queue-management': healthcareImg,
  'school-management': educationImg,
  'hr-payroll': hrImg,
  'payroll-management': hrImg,
  'payment-integration': paymentsImg,
  'mps-eft-switch': paymentsImg,
  'mpesa-paybill': paymentsImg,
  'mobile-banking': mobileImg,
  'mobile-banking-platform': mobileImg,
  'mobile-payment-systems': mobileImg,
  'bulk-sms-platform': mobileImg,
  'bulk-sms-solution': mobileImg,
  'ai-training': aiImg,
  'custom-software': aiImg,
  'api-integration': integrationImg,
  'bank-integration': integrationImg,
  'payment-integration-platform': integrationImg,
};

// ─── Brochure PDF Mapping ──────────────────────────────────
const brochurePdfs = {
  'core-banking': '/Docs/CoreBanking-Brochure.pdf',
  'power-financials': '/Docs/PowerFinancials-Brochure.pdf',
  'sacco-management': '/Docs/SACCOManagement-Brochure.pdf',
  'erp-solutions': '/Docs/ERPSolutions-Brochure.pdf',
  'dynamics-365': '/Docs/Dynamics365-Brochure.pdf',
  'dynamics-nav': '/Docs/DynamicsNAV-Brochure.pdf',
  'hospital-management': '/Docs/HMS_Pitch_Document.pdf',
  'queue-management': '/Docs/QueueManagement-Brochure.pdf',
  'school-management': '/Docs/School_Management_Brochure.pdf',
  'hr-payroll': '/Docs/HRPayroll-Brochure.pdf',
  'payroll-management': '/Docs/PayrollManagement-Brochure.pdf',
  'payment-integration': '/Docs/PaymentIntegration-Brochure.pdf',
  'mps-eft-switch': '/Docs/Micropoint_Payment_Integration_Platform_Website_Brief_v3.docx',
  'mpesa-paybill': '/Docs/MPesaPaybill-Brochure.pdf',
  'mobile-banking': '/Docs/MobileBanking-Brochure.pdf',
  'mobile-banking-platform': '/Docs/MobileBankingPlatform-Brochure.pdf',
  'mobile-payment-systems': '/Docs/MobilePaymentSystems-Brochure.pdf',
  'bulk-sms-platform': '/Docs/BulkSMSPlatform-Brochure.pdf',
  'bulk-sms-solution': '/Docs/BulkSMSSolution-Brochure.pdf',
  'ai-training': '/Docs/AITraining-Brochure.pdf',
  'custom-software': '/Docs/CustomSoftware-Brochure.pdf',
  'api-integration': '/Docs/APIIntegration-Brochure.pdf',
  'bank-integration': '/Docs/BankIntegration-Brochure.pdf',
  'payment-integration-platform': '/Docs/PaymentIntegration-Brochure.pdf',
};

// ─── Helper Functions ─────────────────────────────────
const getIndustryEmoji = (industry) => {
  const emojis = {
    'Banks': '🏦',
    'SACCOs': '🏛️',
    'Microfinance': '💰',
    'Credit Unions': '🤝',
    'Cooperatives': '🤝',
    'Hospitals': '🏥',
    'Clinics': '🏥',
    'Healthcare Providers': '🏥',
    'Schools': '🏫',
    'Colleges': '🏫',
    'Universities': '🎓',
    'Educational Institutions': '🎓',
    'SMEs': '🏢',
    'Manufacturing': '🏭',
    'Distribution': '📦',
    'Services': '💼',
    'Retail': '🛍️',
    'NGOs': '🤲',
    'Government': '🏛️',
    'Payment Processors': '💳',
    'Fintech': '📱',
    'Medical Centers': '🏥',
    'Training Centers': '📚',
    'All Industries': '🌍',
    'Financial Institutions': '🏦',
    'Fintechs': '📱',
    'Payment Service Providers': '💳',
    'Microfinance Institutions': '💰',
    'Payment Services': '💳'
  };
  return emojis[industry] || '📌';
};

const getRelatedIcon = (category) => {
  const icons = {
    'Core Banking': '🏦',
    'ERP': '📊',
    'Payments': '💳',
    'HR': '👥',
    'Automation': '🤖',
    'Mobile': '📱',
    'Communication': '📨',
    'Integration': '🔗',
    'Healthcare': '🏥',
    'Education': '🎓',
    'AI': '🤖',
    'Custom': '⚙️'
  };
  return icons[category] || '📦';
};

const getProductImage = (slug) => {
  return productImages[slug] || coreBankingImg;
};

const getBrochurePdf = (slug) => {
  return brochurePdfs[slug] || null;
};

// ─── Status Badge Component ─────────────────────────────────
const StatusBadge = ({ status }) => {
  const statusConfig = {
    completed: { 
      icon: FaCheck, 
      color: 'text-green-600 bg-green-50 border-green-200',
      label: 'Completed'
    },
    ongoing: { 
      icon: FaSpinner, 
      color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      label: 'Integration Underway'
    },
    attempted: { 
      icon: FaTimes, 
      color: 'text-orange-600 bg-orange-50 border-orange-200',
      label: 'Integration Project'
    }
  };

  const config = statusConfig[status] || statusConfig.completed;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${config.color}`}>
      <Icon className="text-[10px]" />
      {config.label}
    </span>
  );
};

// ─── Component ────────────────────────────────────────
export default function ProductDetail() {
  const { slug } = useParams();
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const product = productsData.find(p => p.slug === slug);

  // Handle brochure download
  const handleBrochureDownload = () => {
    const brochureUrl = getBrochurePdf(slug);
    if (brochureUrl) {
      window.open(brochureUrl, '_blank');
    } else {
      alert('Brochure not available for this product.');
    }
  };

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-20 text-center max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-3xl font-bold text-slate-900">Product Not Found</h1>
          <p className="text-slate-600 mt-3 max-w-md mx-auto text-sm">
            The product you're looking for doesn't exist or may have been moved.
          </p>
          <Link 
            to="/products" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mt-5 transition-colors font-medium text-sm"
          >
            <FaArrowLeft className="text-xs" /> Back to Products
          </Link>
        </motion.div>
      </div>
    );
  }

  const productImage = getProductImage(slug);
  const hasBrochure = getBrochurePdf(slug) !== null;

  // Define why choose points based on product slug
  const getWhyChoosePoints = () => {
    // Check if product has custom whyChoose data
    if (product.whyChoose && product.whyChoose.length > 0) {
      return product.whyChoose.map(item => ({
        icon: getIconForWhyChoose(item.title),
        title: item.title,
        description: item.description
      }));
    }

    if (slug === 'power-financials') {
      return [
        { 
          icon: FaCheckCircle, 
          title: 'All-in-One Compliance-Ready Operations', 
          description: 'Unified members, savings, loans, accounting, and reporting aligned with regulator-ready formats.' 
        },
        { 
          icon: FaMobileAlt, 
          title: 'Bank & M-Pesa Integration', 
          description: 'Direct reconciliation, STK push repayments, and mobile money deposits that cut manual work and cash risk.' 
        },
        { 
          icon: FaCloud, 
          title: 'Cloud-Hosted with Zero Infrastructure Burden', 
          description: 'No servers, automatic backups, and access from any browser or device.' 
        },
        { 
          icon: FaBuilding, 
          title: 'Tailored to Your Microfinance/SACCO', 
          description: 'Products, charges, approval workflows, GL mapping, and PAR buckets configured to match your operations without code changes.' 
        },
      ];
    }
    
    if (slug === 'dynamics-365') {
      return [
        { 
          icon: FaPuzzlePiece, 
          title: 'Tailored & Extensible', 
          description: 'Adapt Business Central to your unique processes, workflows and industry requirements.' 
        },
        { 
          icon: FaLink, 
          title: 'Microsoft Ecosystem', 
          description: 'Connect your business with Microsoft 365, Power BI, Azure and other systems through a unified platform.' 
        },
        { 
          icon: FaUserTie, 
          title: 'Implementation & Local Expertise', 
          description: 'From requirements and migration to customization, training and support, we help you get Business Central working for your organization.' 
        },
      ];
    }

    if (slug === 'hospital-management') {
      return [
        { 
          icon: FaHospital, 
          title: 'Integrated Hospital Operations', 
          description: 'Connect clinical, administrative and financial workflows in one platform—from patient registration and appointments to billing, pharmacy and reporting.' 
        },
        { 
          icon: FaPuzzlePiece, 
          title: 'Tailored to Your Facility', 
          description: 'Configure workflows, departments and processes around how your hospital or healthcare facility actually operates.' 
        },
        { 
          icon: FaLink, 
          title: 'Connected & Extensible', 
          description: 'Integrate with laboratories, pharmacies, payment gateways, SMS and other systems as your healthcare operations grow.' 
        },
      ];
    }

    if (slug === 'school-management') {
      return [
        { 
          icon: FaSchool, 
          title: 'Complete School Operations', 
          description: 'Manage admissions, students, classes, examinations, fees, attendance and communication from one integrated platform.' 
        },
        { 
          icon: FaUserFriends, 
          title: 'Connected School Community', 
          description: 'Keep administrators, teachers, parents and students connected through portals, apps and timely communication.' 
        },
        { 
          icon: FaPuzzlePiece, 
          title: 'Flexible & Extensible', 
          description: 'Adapt the system to your school\'s workflows, policies and reporting requirements as your institution grows.' 
        },
      ];
    }

    if (slug === 'hr-payroll') {
      return [
        { 
          icon: FaPuzzlePiece, 
          title: 'Configurable Earnings & Deductions', 
          description: 'Configure earnings, allowances and deductions to match your organization\'s payroll structure, with flexible rules for recurring and statutory items.' 
        },
        { 
          icon: FaShieldAlt, 
          title: 'Built for Regulatory Payroll', 
          description: 'Automatically handle statutory deductions and contributions such as PAYE, SHIF, NSSF and Housing Levy, with configurable rules to support regulatory changes.' 
        },
        { 
          icon: FaLink, 
          title: 'Integrated Payroll Operations', 
          description: 'Connect payroll with employee records, attendance, leave, banks, tax systems and other HR services for efficient end-to-end processing.' 
        },
      ];
    }

    if (slug === 'mps-eft-switch') {
      return [
        { 
          icon: FaExchangeAlt, 
          title: 'Transaction Switching', 
          description: 'Route and process financial transactions across connected channels and institutions.' 
        },
        { 
          icon: FaNetworkWired, 
          title: 'Multi-Channel Connectivity', 
          description: 'Connect ATM, POS, mobile and other payment channels through a centralized switching layer.' 
        },
        { 
          icon: FaLink, 
          title: 'ISO 8583 & Financial Messaging', 
          description: 'Support established financial transaction messaging standards for interoperable payment environments.' 
        },
      ];
    }
    
    // Default why choose points for other products
    return [
      { 
        icon: FaRocket, 
        title: 'Enterprise-Grade', 
        description: 'Built for scale and reliability' 
      },
      { 
        icon: FaShieldAlt, 
        title: 'Secure & Compliant', 
        description: 'Industry-standard security' 
      },
      { 
        icon: FaHeadset, 
        title: 'Expert Support', 
        description: 'Dedicated support team' 
      },
    ];
  };

  // Helper function to get icon for why choose items
  const getIconForWhyChoose = (title) => {
    const iconMap = {
      'Built Around Your Workflow': FaHandshake,
      'Proven Financial Integrations': FaUniversity,
      'Reconciliation by Design': FaSync,
      'End-to-End Transaction Visibility': FaChartLine,
      'Seamless Multi-Bank Connectivity': FaUniversity,
      'Automated Reconciliation': FaSync,
      'Real-Time Transaction Visibility': FaChartLine,
      'Enterprise-Grade Security': FaLock,
    };
    return iconMap[title] || FaCheckCircle;
  };

  const whyChoosePoints = getWhyChoosePoints();
  const displayFeatures = showAllFeatures ? product.features : product.features.slice(0, 6);
  const hasMoreFeatures = product.features.length > 6;

  // Check if product has integration experience data
  const hasIntegrationExperience = product.integrationExperience && product.integrationExperience.length > 0;
  const hasIntegrationFlow = product.integrationFlow && product.integrationFlow.length > 0;
  const hasSelectedSolutions = product.selectedSolutions && product.selectedSolutions.length > 0;

  return (
    <section className="py-10 md:py-14 bg-white min-h-screen">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* ─── Breadcrumb ─── */}
        <Link 
          to="/products" 
          className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 transition-colors mb-6 text-sm font-medium"
        >
          <FaArrowLeft className="text-xs" /> Back to Products
        </Link>

        {/* ─── Product Header ─── */}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 mb-10">
          
          {/* ─── Image ─── */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative rounded-2xl overflow-hidden h-64 md:h-80 shadow-lg bg-slate-50">
              <img
                src={productImage}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 via-transparent to-transparent" />
              <span className="absolute bottom-4 right-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] px-3 py-1 rounded-full font-semibold shadow-sm border border-white/20">
                {product.category}
              </span>
            </div>
          </motion.div>

          {/* ─── Details ─── */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="flex items-start gap-2 mb-3">
              <span className="inline-block bg-blue-50 text-blue-700 text-[10px] px-3 py-1 rounded-full font-medium border border-blue-100">
                {product.category}
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-3 leading-tight">
              {product.name}
            </h1>
            
            <p className="text-slate-600 text-sm md:text-base leading-relaxed mb-5 max-w-xl">
              {product.description}
            </p>

            {/* ─── Key Features ─── */}
            <div className="mb-5">
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">
                Key Features
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                {displayFeatures.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                    className="flex items-center gap-2.5 text-sm text-slate-700"
                  >
                    <FaCheckCircle className="text-blue-500 text-xs flex-shrink-0" />
                    <span>{feature}</span>
                  </motion.div>
                ))}
              </div>
              
              {hasMoreFeatures && (
                <button
                  onClick={() => setShowAllFeatures(!showAllFeatures)}
                  className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors flex items-center gap-1.5"
                >
                  {showAllFeatures ? (
                    <>Show less <FaChevronUp className="text-xs" /></>
                  ) : (
                    <>+{product.features.length - 6} more <FaChevronDown className="text-xs" /></>
                  )}
                </button>
              )}
            </div>
            
            {/* ─── CTA Buttons ─── */}
            <div className="flex flex-wrap gap-2.5 mb-5">
              <Link to="/demo">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 md:px-8 py-2.5 rounded-lg font-semibold transition-all hover:shadow-md inline-flex items-center gap-2 text-sm">
                  <FaPlay className="text-xs" /> Request Demo
                </button>
              </Link>
              {hasBrochure && (
                <button 
                  onClick={handleBrochureDownload}
                  className="border border-blue-600 text-blue-600 hover:bg-blue-50 px-6 md:px-8 py-2.5 rounded-lg font-semibold transition-all inline-flex items-center gap-2 text-sm"
                >
                  <FaDownload className="text-xs" /> Brochure
                </button>
              )}
              <button className="border border-slate-200 text-slate-500 hover:border-blue-300 hover:text-blue-600 px-3 py-2.5 rounded-lg transition-all inline-flex items-center gap-2 text-sm">
                <FaShare className="text-xs" />
              </button>
            </div>
            
            {/* ─── Quick Stats ─── */}
            <div className="flex flex-wrap gap-5 pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <FaStar className="text-blue-500 text-sm" />
                <span className="text-sm text-slate-600">4.9/5 Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <FaUsers className="text-blue-500 text-sm" />
                <span className="text-sm text-slate-600">150+ Clients</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ─── Industries Served ─── */}
        {product.industries && product.industries.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Industries We Serve</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {product.industries.map((industry, i) => (
                <span key={i} className="flex items-center gap-1.5 bg-white border border-slate-200 px-3 py-1.5 rounded-full text-sm text-slate-700 hover:border-blue-300 transition-all shadow-sm">
                  <span>{getIndustryEmoji(industry)}</span>
                  {industry}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* ─── Integrations ─── */}
        {product.integrations && product.integrations.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Banks & Financial Platforms We've Integrated With</h2>
            </div>
            
            {hasIntegrationExperience ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {product.integrationExperience.map((item, index) => (
                  <div 
                    key={index}
                    className="bg-white border border-slate-200 rounded-lg p-4 hover:border-blue-300 transition-all shadow-sm"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="font-semibold text-slate-900 text-sm">{item.name}</span>
                      <StatusBadge status={item.status} />
                    </div>
                    <p className="text-xs text-slate-500">{item.description}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {product.integrations.map((integration, i) => (
                  <span key={i} className="bg-white border border-slate-200 px-3 py-1.5 rounded-lg text-sm text-slate-700 hover:border-blue-300 transition-all shadow-sm flex items-center gap-1.5">
                    <span className="text-blue-500">🔗</span>
                    {integration}
                  </span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ─── Integration Flow ─── */}
        {hasIntegrationFlow && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.28 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Integration Flow</h2>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
                {product.integrationFlow.map((step, index) => (
                  <div key={index} className="flex items-center">
                    <div className="bg-white rounded-lg px-4 py-2 shadow-sm border border-slate-200 text-sm text-slate-700">
                      {step}
                    </div>
                    {index < product.integrationFlow.length - 1 && (
                      <FaArrowRight className="text-slate-400 mx-1 md:mx-2 text-xs" />
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 text-center mt-4">
                We connect external transaction channels to your core business systems, ensuring transactions are received, validated, matched and posted into the appropriate workflow.
              </p>
            </div>
          </motion.div>
        )}

        {/* ─── Selected Integration Solutions ─── */}
        {hasSelectedSolutions && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-1 h-6 bg-blue-600 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">Selected Integration Solutions Delivered</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {product.selectedSolutions.map((solution, index) => {
                const icons = [FaReceipt, FaFileInvoice, FaUniversity, FaShieldAlt];
                const Icon = icons[index % icons.length];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.08 }}
                    className="bg-white rounded-lg p-5 border border-slate-100 hover:border-blue-200 transition-all shadow-sm hover:shadow-md group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform group-hover:bg-blue-100">
                        <Icon className="text-blue-600 text-lg" />
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm mb-1">{solution.title}</h4>
                        <p className="text-sm text-slate-500 leading-relaxed">{solution.description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* ─── Why Choose ─── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-6 bg-blue-600 rounded-full" />
            <h2 className="text-xl md:text-2xl font-bold text-slate-900">
              Why Choose {product.name}?
            </h2>
          </div>
          <div className={`grid ${whyChoosePoints.length === 4 ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
            {whyChoosePoints.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.08 }}
                  className="bg-white rounded-lg p-5 border border-slate-100 hover:border-blue-200 transition-all shadow-sm hover:shadow-md group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform group-hover:bg-blue-100">
                      <Icon className="text-blue-600 text-lg" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 text-sm mb-1">{item.title}</h4>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* ─── Related Products ─── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-6 bg-blue-600 rounded-full" />
            <h2 className="text-xl md:text-2xl font-bold text-slate-900">Related Solutions</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {productsData
              .filter(p => p.category === product.category && p.id !== product.id)
              .slice(0, 3)
              .map((related, index) => {
                const relatedImage = getProductImage(related.slug);
                return (
                  <Link key={index} to={`/products/${related.slug}`}>
                    <div className="bg-white rounded-lg overflow-hidden border border-slate-100 hover:border-blue-200 transition-all shadow-sm hover:shadow-md">
                      <div className="h-32 overflow-hidden bg-slate-50">
                        <img 
                          src={relatedImage} 
                          alt={related.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2.5 mb-1.5">
                          <span className="text-xl">{getRelatedIcon(related.category)}</span>
                          <h4 className="font-bold text-slate-900 text-sm hover:text-blue-600 transition-colors line-clamp-1">
                            {related.name}
                          </h4>
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-2">
                          {related.description}
                        </p>
                        <div className="mt-2.5 text-blue-600 text-xs font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all">
                          Learn More <FaArrowRight className="text-[10px]" />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </motion.div>

        {/* ─── CTA Section ─── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="relative overflow-hidden rounded-2xl p-8 md:p-10 text-center text-white"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(59,130,246,0.06)_0%,_transparent_70%)]" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <h3 className="text-2xl md:text-3xl font-bold mb-3 text-white">
              Connect Your Financial Systems
            </h3>
            <p className="text-white/60 text-sm mb-6 max-w-lg mx-auto">
              Tell us which banks, payment channels or financial systems you need to connect. Our team can assess the integration requirements and recommend the right approach.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/demo">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-full font-semibold text-sm shadow-lg hover:shadow-blue-600/30 transition-all inline-flex items-center gap-2 hover:-translate-y-0.5">
                  <FaPlay className="text-xs" /> Request a Demo
                </button>
              </Link>
              <Link to="/contact">
                <button className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-full font-semibold text-sm border border-white/10 hover:border-white/20 transition-all inline-flex items-center gap-2">
                  Discuss Your Integration <FaArrowRight className="text-xs" />
                </button>
              </Link>
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
}